import { type FC, useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Smartphone,
  SmartphoneNfc,
  Library,
  Upload,
  CalendarClock,
  Bot,
  Activity,
  HeartPulse,
  Settings,
  Search,
  Command,
  ArrowRight,
} from 'lucide-react';
import { cn } from '../../lib/utils.js';
import { useUIStore } from '../../store/ui-store.js';

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: FC<{ className?: string }>;
  action: () => void;
  keywords: string[];
}

export const CommandPalette: FC = () => {
  const { commandPaletteOpen, setCommandPaletteOpen } = useUIStore();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // The screens in the sidebar, nothing hidden (2026-11-04).
  const commands: CommandItem[] = [
    { id: 'dashboard', label: 'Go to Dashboard', icon: LayoutDashboard, action: () => navigate('/'), keywords: ['dashboard', 'home', 'overview'] },
    { id: 'content', label: 'Go to Media Library', icon: Library, action: () => navigate('/hub/content'), keywords: ['content', 'files', 'media', 'library', 'photos', 'videos'] },
    { id: 'upload', label: 'Upload media', icon: Upload, action: () => navigate('/hub/upload'), keywords: ['upload', 'add', 'media', 'photo', 'video'] },
    { id: 'creators', label: 'Go to Creators', icon: Users, action: () => navigate('/creators'), keywords: ['creators', 'people', 'accounts', 'instagram'] },
    { id: 'posts', label: 'Go to Scheduled Posts', icon: CalendarClock, action: () => navigate('/posts'), keywords: ['posts', 'schedule', 'scheduled', 'publish', 'queue'] },
    { id: 'devices', label: 'Go to Devices', icon: Smartphone, action: () => navigate('/devices'), keywords: ['devices', 'phones', 'android'] },
    { id: 'livewall', label: 'Open the Live Wall', icon: SmartphoneNfc, action: () => navigate('/devices/live-wall'), keywords: ['live', 'wall', 'control', 'screen', 'remote'] },
    { id: 'connectors', label: 'Go to Connectors', icon: Bot, action: () => navigate('/agents'), keywords: ['connector', 'agents', 'pair', 'computer'] },
    { id: 'activity', label: 'Go to Activity', icon: Activity, action: () => navigate('/activity'), keywords: ['activity', 'logs', 'history', 'events'] },
    { id: 'health', label: 'Go to System Health', icon: HeartPulse, action: () => navigate('/health'), keywords: ['health', 'system', 'status'] },
    { id: 'settings', label: 'Go to Settings', icon: Settings, action: () => navigate('/settings'), keywords: ['settings', 'notifications', 'preferences'] },
  ];

  const filtered = query.length === 0
    ? commands
    : commands.filter((c) => {
        const q = query.toLowerCase();
        return c.label.toLowerCase().includes(q) || c.keywords.some((k) => k.includes(q));
      });

  const executeSelected = useCallback(() => {
    if (filtered.length > 0 && filtered[selectedIndex] !== undefined) {
      filtered[selectedIndex].action();
      setCommandPaletteOpen(false);
      setQuery('');
    }
  }, [filtered, selectedIndex, setCommandPaletteOpen]);

  useEffect(() => {
    if (commandPaletteOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [commandPaletteOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Keyboard navigation
  useEffect(() => {
    if (!commandPaletteOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setCommandPaletteOpen(false);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        executeSelected();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [commandPaletteOpen, filtered, selectedIndex, executeSelected, setCommandPaletteOpen]);

  if (!commandPaletteOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
      onClick={() => setCommandPaletteOpen(false)}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Dialog */}
      <div
        className="relative w-full max-w-lg rounded-xl border border-slate-800 bg-slate-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center border-b border-slate-800 px-4">
          <Search className="h-4 w-4 text-slate-500" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages and actions..."
            className="h-12 flex-1 bg-transparent px-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none"
          />
          <kbd className="flex items-center gap-0.5 rounded border border-slate-700 bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-500">
            <Command className="h-2.5 w-2.5" />K
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-72 overflow-y-auto p-2">
          {filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">No results found</p>
          ) : (
            filtered.map((item, index) => (
              <button
                key={item.id}
                onClick={() => {
                  item.action();
                  setCommandPaletteOpen(false);
                  setQuery('');
                }}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors',
                  index === selectedIndex ? 'bg-blue-600/20 text-blue-300' : 'text-slate-300 hover:bg-slate-800',
                )}
              >
                <item.icon className="h-4 w-4 shrink-0" />
                <span className="flex-1">{item.label}</span>
                <ArrowRight className="h-3.5 w-3.5 text-slate-600" />
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 border-t border-slate-800 px-4 py-2.5">
          <div className="flex items-center gap-1.5 text-[10px] text-slate-600">
            <kbd className="rounded border border-slate-700 bg-slate-800 px-1 py-0.5">↑↓</kbd>
            <span>Navigate</span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-slate-600">
            <kbd className="rounded border border-slate-700 bg-slate-800 px-1 py-0.5">↵</kbd>
            <span>Open</span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-slate-600">
            <kbd className="rounded border border-slate-700 bg-slate-800 px-1 py-0.5">Esc</kbd>
            <span>Close</span>
          </div>
        </div>
      </div>
    </div>
  );
};
